import { useMemo } from 'react';
import { useCountry } from '@/contexts/CountryContext';
import { CountryConfig } from '@/types/country';

export interface AddressValidationRules {
  // Postal code/ZIP code validation
  postalCodePattern: RegExp;
  postalCodeLabel: string;
  postalCodePlaceholder: string;
  postalCodeErrorMessage: string;
  
  // Phone number validation
  phonePattern?: RegExp;
  phoneLabel: string;
  phoneErrorMessage: string;
  
  // Address fields requirements
  requiredFields: string[];
  optionalFields: string[];
  
  // Field labels and placeholders
  fieldLabels: Record<string, string>;
  fieldPlaceholders: Record<string, string>;
  
  // Additional validation rules
  streetNumberPattern?: RegExp;
  streetNumberErrorMessage: string;
}

interface CountryAddressValidation {
  [key: string]: AddressValidationRules;
}

// Country-specific address validation rules
const COUNTRY_ADDRESS_RULES: CountryAddressValidation = {
  'GB': {
    postalCodePattern: /^([A-Z][A-HJ-Y]?\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A{2})$/i,
    postalCodeLabel: 'Postcode',
    postalCodePlaceholder: 'SW1A 1AA',
    postalCodeErrorMessage: 'Invalid UK postcode format (e.g., SW1A 1AA)',
    
    phonePattern: /^(\+44\s?|0)(\d{4}\s?\d{6}|\d{3}\s?\d{3}\s?\d{4}|\d{2}\s?\d{4}\s?\d{4})$/,
    phoneLabel: 'Phone Number',
    phoneErrorMessage: 'Invalid UK phone number format',
    
    requiredFields: ['line1', 'city', 'postcode'],
    optionalFields: ['line2', 'county', 'country'],
    
    fieldLabels: {
      line1: 'Address Line 1',
      line2: 'Address Line 2',
      city: 'Town/City',
      county: 'County',
      postcode: 'Postcode',
      country: 'Country',
      streetNumber: 'Street Number',
      route: 'Street Name',
      locality: 'Town/City',
      state: 'County',
      postalCode: 'Postcode',
    },
    
    fieldPlaceholders: {
      line1: 'Street address',
      line2: 'Apartment, suite, etc.',
      city: 'London',
      county: 'Greater London',
      postcode: 'SW1A 1AA',
      streetNumber: '123',
      route: 'High Street',
      locality: 'London',
      state: 'Greater London',
      postalCode: 'SW1A 1AA',
    },
    
    streetNumberPattern: /^[0-9A-Za-z]+$/,
    streetNumberErrorMessage: 'Street number must contain only numbers and letters',
  },
  
  'US': {
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodeLabel: 'ZIP Code',
    postalCodePlaceholder: '12345',
    postalCodeErrorMessage: 'Invalid ZIP code format (e.g., 12345 or 12345-6789)',
    
    phonePattern: /^(\+1\s?)?(\(\d{3}\)|\d{3})[\s\-]?\d{3}[\s\-]?\d{4}$/,
    phoneLabel: 'Phone Number',
    phoneErrorMessage: 'Invalid US phone number format',
    
    requiredFields: ['line1', 'city', 'state', 'zipCode'],
    optionalFields: ['line2', 'country'],
    
    fieldLabels: {
      line1: 'Address Line 1',
      line2: 'Address Line 2',
      city: 'City',
      state: 'State',
      zipCode: 'ZIP Code',
      country: 'Country',
      streetNumber: 'Street Number',
      route: 'Street Name',
      locality: 'City',
      postalCode: 'ZIP Code',
    },
    
    fieldPlaceholders: {
      line1: 'Street address',
      line2: 'Apartment, suite, etc.',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      streetNumber: '123',
      route: 'Main Street',
      locality: 'New York',
      postalCode: '10001',
    },
    
    streetNumberPattern: /^[0-9A-Za-z\-\/\s]+$/,
    streetNumberErrorMessage: 'Street number must contain only numbers, letters, hyphens, and spaces',
  },
};

// Default fallback rules (based on UK)
const DEFAULT_RULES: AddressValidationRules = COUNTRY_ADDRESS_RULES['GB'];

export interface AddressValidationResult {
  isValid: boolean;
  error: string;
}

export interface AddressValidationHook {
  // Validation functions
  validatePostalCode: (postalCode: string) => AddressValidationResult;
  validatePhoneNumber: (phoneNumber: string) => AddressValidationResult;
  validateStreetNumber: (streetNumber: string) => AddressValidationResult;
  validateRequiredField: (fieldName: string, value: string) => AddressValidationResult;
  
  // Validation rules for current country
  rules: AddressValidationRules;
  
  // Helper functions
  getFieldLabel: (fieldName: string) => string;
  getFieldPlaceholder: (fieldName: string) => string;
  isFieldRequired: (fieldName: string) => boolean;
  
  // Complete address validation
  validateAddress: (address: any) => Record<string, string>;
}

/**
 * Hook for country-specific address validation
 * Replaces hardcoded GB validation with dynamic country-based validation
 */
export const useAddressValidation = (forceCountry?: string): AddressValidationHook => {
  // Use a try-catch to handle cases where CountryProvider is not available (e.g., tests, employer users)
  let currentCountry: CountryConfig | null = null;
  try {
    const countryContext = useCountry();
    currentCountry = countryContext.currentCountry;
  } catch (error) {
    // CountryProvider not available, will default to GB rules
    console.debug('CountryProvider not available, defaulting to GB address validation rules');
  }
  
  const rules = useMemo(() => {
    const countryCode = forceCountry || currentCountry?.code || 'GB';
    return COUNTRY_ADDRESS_RULES[countryCode] || DEFAULT_RULES;
  }, [forceCountry, currentCountry]);
  
  const validatePostalCode = (postalCode: string): AddressValidationResult => {
    if (!postalCode || !postalCode.trim()) {
      return { isValid: false, error: `${rules.postalCodeLabel} is required` };
    }
    
    const cleanPostalCode = postalCode.replace(/\s/g, '');
    if (!rules.postalCodePattern.test(cleanPostalCode)) {
      return { isValid: false, error: rules.postalCodeErrorMessage };
    }
    
    return { isValid: true, error: '' };
  };
  
  const validatePhoneNumber = (phoneNumber: string): AddressValidationResult => {
    if (!phoneNumber || !phoneNumber.trim()) {
      return { isValid: true, error: '' }; // Phone is usually optional
    }
    
    if (rules.phonePattern && !rules.phonePattern.test(phoneNumber)) {
      return { isValid: false, error: rules.phoneErrorMessage };
    }
    
    return { isValid: true, error: '' };
  };
  
  const validateStreetNumber = (streetNumber: string): AddressValidationResult => {
    if (!streetNumber || !streetNumber.trim()) {
      return { isValid: true, error: '' }; // Street number is usually optional
    }
    
    if (rules.streetNumberPattern && !rules.streetNumberPattern.test(streetNumber)) {
      return { isValid: false, error: rules.streetNumberErrorMessage };
    }
    
    return { isValid: true, error: '' };
  };
  
  const validateRequiredField = (fieldName: string, value: string): AddressValidationResult => {
    if (rules.requiredFields.includes(fieldName) && (!value || !value.trim())) {
      const label = rules.fieldLabels[fieldName] || fieldName;
      return { isValid: false, error: `${label} is required` };
    }
    
    return { isValid: true, error: '' };
  };
  
  const getFieldLabel = (fieldName: string): string => {
    return rules.fieldLabels[fieldName] || fieldName;
  };
  
  const getFieldPlaceholder = (fieldName: string): string => {
    return rules.fieldPlaceholders[fieldName] || '';
  };
  
  const isFieldRequired = (fieldName: string): boolean => {
    return rules.requiredFields.includes(fieldName);
  };
  
  const validateAddress = (address: any): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    // Validate required fields
    rules.requiredFields.forEach(fieldName => {
      const value = address[fieldName];
      const result = validateRequiredField(fieldName, value);
      if (!result.isValid) {
        errors[fieldName] = result.error;
      }
    });
    
    // Validate postal code specifically
    const postalCodeField = address.postalCode || address.postal_code || address.postcode || address.zipCode;
    if (postalCodeField) {
      const result = validatePostalCode(postalCodeField);
      if (!result.isValid) {
        // Use the appropriate field name based on country
        const fieldName = rules.postalCodeLabel === 'ZIP Code' ? 'zipCode' : 'postcode';
        errors[fieldName] = result.error;
      }
    }
    
    // Validate street number if present
    const streetNumber = address.streetNumber || address.street_number;
    if (streetNumber) {
      const result = validateStreetNumber(streetNumber);
      if (!result.isValid) {
        errors['streetNumber'] = result.error;
      }
    }
    
    // Validate phone number if present
    const phoneNumber = address.phoneNumber || address.phone;
    if (phoneNumber) {
      const result = validatePhoneNumber(phoneNumber);
      if (!result.isValid) {
        errors['phoneNumber'] = result.error;
      }
    }
    
    return errors;
  };
  
  return {
    validatePostalCode,
    validatePhoneNumber,
    validateStreetNumber,
    validateRequiredField,
    rules,
    getFieldLabel,
    getFieldPlaceholder,
    isFieldRequired,
    validateAddress,
  };
}; 